import gql from 'graphql-tag';

import { Guest, CheckinGuest, GuestListItem, CheckinGuestListItem } from 'types/guest.types';
import { QUESTION_RESPONSE_FRAGMENT } from 'types/question-response.types';
import { User } from 'types/user.types';

export interface GuestsQueryResponse {
  guests: {
    guests: Guest[];
    pages: number;
  };
}

export interface GuestsQueryVariables {
  eventId: number;
  statuses: string[];
  checkedIn?: boolean;
  searchText: string;
  departments: string[];
  currentPage: number;
  attendanceFilters: string[];
}

export interface GuestListQueryResponse {
  guestList: {
    pages: number;
    page: number;
    items: GuestListItem[];
  };
}

export interface GuestListQueryVariables {
  eventId: number;
  statuses?: string[];
  departments?: string[];
  attendanceFilters?: string[];
  searchText?: string;
  currentPage?: number;
  pageSize?: number;
}

export const INSIGHTS_GUEST_LIST_QUERY = gql`
  query (
    $eventId: Int!
    $statuses: [String]
    $departments: [String]
    $attendanceFilters: [String]
    $searchText: String
    $currentPage: Int
    $pageSize: Int
  ) {
    guestList(
      guestSearch: {
        eventId: $eventId
        statuses: $statuses
        departments: $departments
        attendanceFilters: $attendanceFilters
        searchText: $searchText
        page: $currentPage
        perPage: $pageSize
      }
    ) {
      pages
      page
      items {
        ... on Guest {
          __typename
          gid
          email
          userId
          department
          firstName
          lastName
          status
          isAttended
          zoomStatus
          meetStatus
          surveyResponses
          lastUpdatedAt
          associatedDistrolists {
            id
            name
          }
        }
        ... on PlusOneGuest {
          __typename
          plusOneId
          eventId
          firstName
          lastName
          questionnaireResponses {
            ...QuestionResponseFields
          }
          status
          isCheckedIn
          owner {
            gid
            firstName
            lastName
          }
          lastUpdatedAt
        }
      }
    }
  }
  ${QUESTION_RESPONSE_FRAGMENT}
`;

export const INSIGHTS_GUESTS_QUERY = gql`
  query (
    $eventId: Int!
    $statuses: [String]
    $departments: [String]
    $attendanceFilters: [String]
    $searchText: String
    $currentPage: Int
    $pageSize: Int
  ) {
    guests(
      guestSearch: {
        eventId: $eventId
        statuses: $statuses
        departments: $departments
        attendanceFilters: $attendanceFilters
        searchText: $searchText
        page: $currentPage
        perPage: $pageSize
      }
    ) {
      guests {
        gid
        email
        userId
        department
        firstName
        lastName
        status
        isAttended
        zoomStatus
        meetStatus
        surveyResponses
        lastUpdatedAt
        associatedDistrolists {
          id
          name
        }
      }
      pages
    }
  }
`;

export interface CheckinGuestListQueryResponse {
  checkinGuestList: {
    guests: CheckinGuestListItem[];
    otherEmployees: User[];
    pages: number;
  };
}

export interface CheckinGuestListQueryVariables {
  eventId: number;
  checkedIn?: boolean | null;
  searchText?: string;
  includeNonGuests?: boolean;
  currentPage: number;
  pageSize: number;
}

export const INSIGHTS_CHECKIN_GUEST_LIST_QUERY = gql`
  query (
    $eventId: Int!
    $checkedIn: Boolean
    $searchText: String
    $includeNonGuests: Boolean
    $currentPage: Int
    $pageSize: Int
  ) {
    checkinGuestList(
      checkinGuestInput: {
        eventId: $eventId
        checkedIn: $checkedIn
        searchText: $searchText
        includeNonGuests: $includeNonGuests
        page: $currentPage
        perPage: $pageSize
      }
    ) {
      guests {
        ... on Guest {
          __typename
          userId
          firstName
          lastName
          profilePictureUrl
          status
          isCheckedIn
          isSelfCheckin
          isZoomAttended
          isMeetAttended
          email
        }
        ... on PlusOneGuest {
          __typename
          plusOneId
          firstName
          lastName
          status
          isCheckedIn
          isSelfCheckin
          owner {
            gid
            firstName
            lastName
          }
        }
      }
      otherEmployees {
        id
        firstName
        lastName
        profilePictureUrl
        email
      }
      pages
    }
  }
`;

export interface GuestsCheckinQueryResponse {
  guestCheckinStats: {
    guestsToBeCheckedIn: number;
    guestsCheckedIn: number;
  };
  checkinGuests: {
    guests: CheckinGuest[];
    otherEmployees: User[];
    pages: number;
  };
}

export interface GuestsCheckinQueryVariables {
  eventId: number;
  checkedIn?: boolean | null;
  searchText?: string;
  includeNonGuests?: boolean;
  currentPage: number;
  pageSize: number;
}

export const INSIGHTS_GUESTS_CHECKIN_QUERY = gql`
  query (
    $eventId: Int!
    $checkedIn: Boolean
    $searchText: String
    $includeNonGuests: Boolean
    $currentPage: Int
    $pageSize: Int
  ) {
    checkinGuests(
      checkinGuestInput: {
        eventId: $eventId
        checkedIn: $checkedIn
        searchText: $searchText
        includeNonGuests: $includeNonGuests
        page: $currentPage
        perPage: $pageSize
      }
    ) {
      guests {
        userId
        firstName
        lastName
        profilePictureUrl
        status
        isCheckedIn
        isSelfCheckin
        isZoomAttended
        isMeetAttended
        status
        email
      }
      otherEmployees {
        id
        firstName
        lastName
        profilePictureUrl
        email
      }
      pages
    }
  }
`;

export interface GuestsCheckinStatsQueryResponse {
  guestCheckinStats: {
    guestsToBeCheckedIn: number;
    guestsCheckedIn: number;
  };
}

export interface GuestsCheckinStatsQueryVariables {
  eventId: number;
}

export const INSIGHTS_GUESTS_CHECKIN_STATS_QUERY = gql`
  query ($eventId: Int!) {
    guestCheckinStats(eventId: $eventId) {
      guestsToBeCheckedIn
      guestsCheckedIn
    }
  }
`;

export interface GuestDepartmentsQueryResponse {
  guestDepartments: { allGuestDepartments: string[] };
}

export interface GuestDepartmentsQueryVariables {
  eventId: number;
}

export const INSIGHTS_GUEST_DEPARTMENTS = gql`
  query ($eventId: Int!) {
    guestDepartments(eventId: $eventId) {
      allGuestDepartments
    }
  }
`;
